import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from "react";
import { DEMO_DEAL, BLANK_DEAL, DEFAULT_RECAP, DEFAULT_ROLLUP, verdict as verdictOf, scopeOptions, scopedModel, scenarioModel, SCOPE_PLATFORM, defaultScope, coerceModel, coerceField } from "./model";
import { streamPost, aiApi, dealsApi, platformsApi } from "./api";
import { dealKeyOf, setCurrentDealKey } from "./dealctx";
import { toast } from "sonner";

const DealContext = createContext(null);

// ---- shared AI-finder helpers (module level, pure) ----
const parseLooseInvestors = (t) => {
  let s = (t || "").trim();
  if (s.startsWith("```")) s = s.replace(/^```(json)?/i, "").replace(/```\s*$/, "");
  const a = s.indexOf("{");
  if (a !== -1) s = s.slice(a);
  try {
    const b = s.lastIndexOf("}");
    return JSON.parse(s.slice(0, b + 1));
  } catch (e) { /* fall through to salvage */ }
  const lc = s.lastIndexOf("},");
  if (lc !== -1) {
    const cand = s.slice(0, lc + 1) + "]}";
    try { return JSON.parse(cand); } catch (e2) { /* ignore */ }
  }
  const out = [];
  const re = /\{[^{}]*\}/g; let mm;
  while ((mm = re.exec(s))) { try { const o = JSON.parse(mm[0]); if (o && o.name) out.push(o); } catch (_) {} }
  return { investors: out };
};

const parseLenders = (full) => {
  const clean = (full || "").replace(/```json/gi, "").replace(/```/g, "").trim();
  try {
    const s = clean.indexOf("{"), e = clean.lastIndexOf("}");
    const arr = JSON.parse(clean.slice(s, e + 1)).lenders;
    if (Array.isArray(arr) && arr.length) return arr;
  } catch (_) {}
  const out = [];
  const re = /\{[^{}]*\}/g;
  let mm;
  while ((mm = re.exec(clean))) {
    try { const o = JSON.parse(mm[0]); if (o && o.name) out.push(o); } catch (_) {}
  }
  return out;
};

const INV_SEGMENTS = [
  "independent sponsors & search funds",
  "family offices & independent-sponsor LPs",
  "lower-middle-market PE firms",
  "SBIC & mezzanine / private-credit funds",
  "national funds active in this sector",
  "regional & local investors near the deal",
];
const INV_MORE_SEGMENTS = [INV_SEGMENTS[0], INV_SEGMENTS[2]];

const LEND_SEGMENTS = [
  "SBA 7(a) preferred lenders nationwide",
  "community & regional banks and credit unions",
  "SBIC funds, mezzanine & private-credit lenders",
];
const LEND_MORE_SEGMENTS = ["equipment finance & non-bank direct lenders", "national SMB banks"];

const dedupeSort = (arr) => arr.sort((a, b) => (b.fitScore || 0) - (a.fitScore || 0));
const mergeUnique = (base, batches) => {
  const seen = new Set(base.map((p) => (p.name || "").toLowerCase().trim()));
  const acc = [...base];
  for (const batch of batches) for (const p of batch) { const k = (p.name || "").toLowerCase().trim(); if (k && !seen.has(k)) { seen.add(k); acc.push(p); } }
  return acc;
};

async function invBatch(M, existing, segment) {
  let full = "";
  await streamPost("/ai/find-investors", { deal: M, exclude: existing.map((p) => p.name).filter(Boolean), segment }, (c, f) => { full = f; });
  return parseLooseInvestors(full).investors || [];
}
async function lendBatch(M, industry, existing, segment) {
  let full = "";
  await streamPost("/ai/find-lenders", { deal: { ...M, industry }, exclude: existing.map((l) => l.name).filter(Boolean), segment }, (c, f) => { full = f; });
  return parseLenders(full);
}

const WS_KEY = "xyz_workspace_v1";
const readWorkspace = () => { try { return JSON.parse(localStorage.getItem(WS_KEY) || "null"); } catch { return null; } };

export function DealProvider({ children }) {
  const ws = useRef(readWorkspace()).current;
  const [M, setM] = useState(() => coerceModel(ws?.M ? { ...DEMO_DEAL, ...ws.M } : { ...DEMO_DEAL }));
  const [dealId, setDealId] = useState(ws?.dealId || null);
  const [dealName, setDealName] = useState(ws?.dealName || DEMO_DEAL.dealName);
  const [saveState, setSaveState] = useState("saved");
  const dirtyRef = useRef(false);
  const [platforms, setPlatforms] = useState([]);
  const refreshPlatforms = useCallback(() => platformsApi.list().then(setPlatforms).catch(() => {}), []);
  useEffect(() => { refreshPlatforms(); }, [refreshPlatforms]);
  const [scenario, setScenarioRaw] = useState("base");
  const [scenFlash, setScenFlash] = useState(false);
  // Picking a case refreshes every number on screen, so flash the page once to show it landed.
  const setScenario = useCallback((s) => {
    setScenarioRaw(s);
    setScenFlash(true);
    setTimeout(() => setScenFlash(false), 480);
  }, []);
  // View scope: this deal only / consolidated roll-up / a single add-on. Falls back to the platform when the roll-up has no add-ons.
  // The deal decides the view: an explicit pick on the model wins, otherwise the model's own default.
  // A stale `scope` left in localStorage must never win, or the app and the deal room disagree.
  const [scopeRaw, setScopeRaw] = useState(defaultScope(ws?.M || {}));
  const scopeOpts = useMemo(() => scopeOptions(M), [M]);
  const scope = scopeOpts && scopeOpts.some((o) => o.id === scopeRaw) ? scopeRaw : SCOPE_PLATFORM;
  // The chosen case (base / upside / downside) applies app-wide: every page reads V, so the toggle
  // flows through Dashboard, S&U, P&L, Returns, Sensitivity, Roll-Up, the teaser and the deal room.
  const V = useMemo(() => scenarioModel(scopedModel(M, scope), scenario), [M, scope, scenario]);
  const [prospects, setProspects] = useState([]);
  const [prospectDrafts, setProspectDrafts] = useState({});
  const [lenders, setLenders] = useState([]);
  const [industry, setIndustry] = useState(M.industry || "");
  useEffect(() => { setIndustry(M.industry || ""); }, [M.industry]);
  const [active, setActive] = useState(ws?.active || "dashboard");
  // Deep-link target for the Assumptions rail (every deal input lives there).
  const [asmpSection, setAsmpSection] = useState(null);

  // AI finder state
  const [findingInvestors, setFindingInvestors] = useState(false);
  const [findingLenders, setFindingLenders] = useState(false);
  const [market, setMarket] = useState(ws?.market || null);
  const [marketLoading, setMarketLoading] = useState(false);
  const marketRunningRef = useRef(false);
  const marketSig = (d) => [d.codeName, d.businessType, d.location, d.dealName].join("|");

  const setField = useCallback((key, value) => {
    dirtyRef.current = true; setSaveState("unsaved");
    setM((prev) => ({ ...prev, [key]: coerceField(key, value) }));
  }, []);

  const patch = useCallback((obj) => {
    dirtyRef.current = true; setSaveState("unsaved");
    setM((prev) => ({ ...prev, ...coerceModel(obj) }));
  }, []);

  // The deal view is part of the deal, not a per-browser preference: it is saved on the model so
  // every screen and the investor deal room (which reads the live saved model) show the same deal.
  const setScope = useCallback((id) => { setScopeRaw(id); patch({ viewScope: id }); }, [patch]);

  // Stable per-deal key (used by Deal Room shares) — independent of pipeline id / name.
  useEffect(() => {
    // patch(), not setM(), so the key is marked dirty and autosaved — otherwise every reload
    // minted a new key and anything filed under it (Intelligence runs, rooms) looked empty.
    if (!M.dealKey) patch({ dealKey: `dk-${Math.random().toString(36).slice(2, 10)}` });
  }, [M.dealKey, patch]);

  // 1) Browser persistence — instant, survives reloads / dropped connections.
  useEffect(() => {
    setCurrentDealKey(dealKeyOf(dealId, M));
    try { localStorage.setItem(WS_KEY, JSON.stringify({ M, market, dealId, dealName, active, scope, savedAt: new Date().toISOString() })); } catch { /* quota */ }
  }, [M, market, dealId, dealName, active, scope]);

  // 2) Server autosave to the Pipeline — debounced; creates the deal on first edit.
  const saveTimer = useRef(null);
  const savingRef = useRef(false);
  const syncNow = useCallback(async (force = false) => {
    if (!dirtyRef.current && !force) return;
    if (savingRef.current) return;
    savingRef.current = true; setSaveState("saving");
    try {
      const payload = { name: dealName || M.dealName || "Untitled Deal", model: M, verdict: verdictOf(M) };
      if (dealId) await dealsApi.update(dealId, payload);
      else { const d = await dealsApi.create({ ...payload, stage: "Underwriting" }); setDealId(d.id); }
      dirtyRef.current = false; setSaveState("saved");
    } catch { setSaveState(navigator.onLine ? "error" : "offline"); }
    finally { savingRef.current = false; }
  }, [M, dealId, dealName]);
  useEffect(() => {
    if (!dirtyRef.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => syncNow(), 4000);
    return () => clearTimeout(saveTimer.current);
  }, [M, dealName, syncNow]);
  const [saveTick, setSaveTick] = useState(0);
  const requestSave = useCallback(() => setSaveTick((t) => t + 1), []);
  useEffect(() => { if (saveTick) syncNow(true); }, [saveTick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const back = () => { if (dirtyRef.current) syncNow(); };
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [syncNow]);
  useEffect(() => {
    if (ws?.M && ws.savedAt) {
      const mins = Math.max(0, Math.round((Date.now() - new Date(ws.savedAt).getTime()) / 60000));
      toast.success(`Resumed ${ws.dealName || ws.M.dealName} · saved ${mins < 1 ? "just now" : `${mins} min ago`}`, { duration: 4000 });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewDeal = useCallback(async () => {
    if (dirtyRef.current) await syncNow(true);
    const name = `New Deal – ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    dirtyRef.current = false;
    setM(BLANK_DEAL(name)); setDealId(null); setDealName(name); setMarket(null);
    setProspects([]); setProspectDrafts({}); setLenders([]);
    setSaveState("unsaved");
    setActive("assumptions"); setAsmpSection("upload-parse"); window.scrollTo(0, 0);
    toast.success("New deal started — upload a CIM, P&L or tax return to begin");
  }, [syncNow]);

  const loadModel = useCallback((newM, id = null, name = null, savedMarket = null) => {
    dirtyRef.current = false; setSaveState("saved");
    setM(coerceModel({ ...DEMO_DEAL, ...newM }));
    setScopeRaw(defaultScope(newM));
    setDealId(id);
    setDealName(name || newM.dealName || "Untitled Deal");
    setMarket(savedMarket && Object.keys(savedMarket).length ? savedMarket : null);   // restore the deal's saved market intel
  }, []);

  // Persist market intelligence with the deal so it survives reloads and flows into deal rooms.
  const marketSavedRef = useRef(null);
  useEffect(() => {
    if (!dealId || !market || !market._sig || marketLoading) return;
    const sig = JSON.stringify([dealId, market._sig, Object.keys(market).sort().join(","), (market.comps || []).length, (market.consolidators || []).length]);
    if (marketSavedRef.current === sig) return;
    marketSavedRef.current = sig;
    dealsApi.update(dealId, { market }).catch(() => {});
  }, [market, dealId, marketLoading]);

  const navigate = useCallback((id, section = null) => { setActive(id); if (section) setAsmpSection(section); window.scrollTo(0, 0); }, []);

  // Recapitalize a platform we already own — seeds the model from the platform record.
  const startRecap = useCallback(async (P, r = {}, goTo = "assumptions") => {
    await syncNow(true);
    const year = new Date().getFullYear();
    const ebitda = Math.round(r.ebitda || P.ebitda || 0), mult = P.multiplePaid || 5;
    const origEquity = Math.max(0, Math.round(((P.ebitda || 0) * (P.multiplePaid || 0)) - (P.debt || 0)));
    const name = `${P.name} — Recapitalization`;
    loadModel({ ...BLANK_DEAL(name), dealName: name, companyName: P.name, industry: P.industry || "", sde: ebitda, askPrice: ebitda * mult, salary: 0,
      ttmRevenue: P.revenue || 0, rate: P.debtRate || 10, loanYrs: P.debtYears || 10, snAmt: 0, rollPct: 0, txnType: "recap", platformRole: "platform", platformId: P.id,
      recap: { ...DEFAULT_RECAP, parentName: P.name, platformId: P.id, recapMult: mult, existingDebt: Math.round(r.debt ?? P.debt ?? 0), newDebt: Math.round(ebitda * 3),
        cashOutPct: 50, warChest: Math.round(ebitda), origEquity, origYears: Math.max(1, year - (P.acquiredYear || year - 3)), legacyDist: 0, syncRollup: true },
      rollup: { ...DEFAULT_ROLLUP, exitMultiple: Math.max(mult + 2, 7), companies: [{ name: P.name, ebitda, multiple: mult, year: 0 }, { name: "Add-on 1", ebitda: Math.round(ebitda * 0.25), multiple: Math.max(3, mult - 1), year: 1 }, { name: "Add-on 2", ebitda: Math.round(ebitda * 0.25), multiple: Math.max(3, mult - 1), year: 2 }] },
    }, null, name);
    patch({ txnType: "recap" });
    toast.success(`Recapitalization of ${P.name} started — ${goTo === "upload" ? "upload its latest financials under Assumptions \u2192 Upload & Parse" : "set the recap terms in Assumptions"}`);
    if (goTo === "upload") navigate("assumptions", "upload-parse"); else if (goTo) navigate(goTo);
  }, [syncNow, loadModel, patch, navigate]);

  const runMarket = useCallback(async () => {
    if (marketRunningRef.current) return;
    marketRunningRef.current = true;
    setMarketLoading(true);
    // Run the three AI sources INDEPENDENTLY so a slow/failed Claude market call
    // never blocks the live Perplexity comps & TAM — each renders as it lands.
    const merge = (patch) => setMarket((prev) => {
      const next = { ...(prev || {}), ...patch };
      if (patch.tam) next.tam = { ...((prev && prev.tam) || {}), ...patch.tam };
      return next;
    });
    const jobs = [
      aiApi.market({ deal: M })
        .then((res) => {
          if (!res) return null;
          // base narrative/consolidators; anything Perplexity already set (comps/tam) wins
          setMarket((prev) => ({
            ...res,
            ...(prev || {}),
            tam: { ...(res.tam || {}), ...((prev && prev.tam) || {}) },
          }));
          if (res.sector) setM((prev) => (prev.industry ? prev : { ...prev, industry: res.sector }));
          return "market";
        })
        .catch((e) => { console.error("market failed", e); return null; }),
      aiApi.tam({ deal: M })
        .then((t) => {
          if (t && t.tam && Object.keys(t.tam).length) { merge({ tam: t.tam, tamCitations: t.citations || [] }); return "tam"; }
          return null;
        })
        .catch((e) => { console.error("tam failed", e); return null; }),
      aiApi.comps({ deal: M })
        .then((c) => {
          if (c && (c.comps || []).length) { merge({ comps: c.comps, compCitations: c.citations || [] }); return "comps"; }
          return null;
        })
        .catch((e) => { console.error("comps failed", e); return null; }),
      aiApi.consolidators({ deal: M })
        .then((c) => {
          if (c && (c.consolidators || []).length) { merge({ consolidators: c.consolidators, consolidatorCitations: c.citations || [] }); return "consolidators"; }
          return null;
        })
        .catch((e) => { console.error("consolidators failed", e); return null; }),
    ];
    try {
      const results = await Promise.allSettled(jobs);
      const okCount = results.filter((r) => r.status === "fulfilled" && r.value).length;
      if (okCount === 0) toast.error("Market intelligence failed — please try again");
      else if (okCount < jobs.length) toast.message("Partial results — some AI sources timed out; hit Refresh to retry.");
      // stamp the intel with the deal it was generated for, to detect staleness later
      if (okCount > 0) setMarket((prev) => (prev ? { ...prev, _sig: marketSig(M) } : prev));
    } finally {
      setMarketLoading(false);
      marketRunningRef.current = false;
    }
  }, [M]);

  // Market intel is stale if the deal's identity changed since it was generated.
  const marketStale = !!(market && market._sig && market._sig !== marketSig(M));

  const runInvestors = useCallback(async () => {
    runMarket(); // fire market comps in parallel with the investor search
    setFindingInvestors(true); setProspects([]); setProspectDrafts({});
    try {
      const batches = await Promise.all(INV_SEGMENTS.map((seg) => invBatch(M, [], seg).catch(() => [])));
      const acc = dedupeSort(mergeUnique([], batches));
      setProspects(acc);
      if (acc.length) toast.success(`Found ${acc.length} matched investors`);
      else toast.error("Investor search failed — try again");
      return acc;
    } finally { setFindingInvestors(false); }
  }, [M, runMarket]);

  const findMoreInvestors = useCallback(async () => {
    setFindingInvestors(true);
    try {
      const batches = await Promise.all(INV_MORE_SEGMENTS.map((seg) => invBatch(M, prospects, seg).catch(() => [])));
      const acc = dedupeSort(mergeUnique(prospects, batches));
      const added = acc.length - prospects.length;
      setProspects(acc);
      toast.success(`Added ${added} more (${acc.length} total)`);
    } finally { setFindingInvestors(false); }
  }, [M, prospects]);

  const runLenders = useCallback(async () => {
    runMarket(); // fire market comps in parallel with the lender search
    setFindingLenders(true); setLenders([]);
    try {
      const batches = await Promise.all(LEND_SEGMENTS.map((seg) => lendBatch(M, industry, [], seg).catch(() => [])));
      const acc = []; const seen = new Set();
      batches.forEach((b) => { for (const l of b) { const k = (l.name || "").toLowerCase().trim(); if (k && !seen.has(k)) { seen.add(k); acc.push(l); } } });
      setLenders(acc);
      if (acc.length) toast.success(`Found ${acc.length} matched lenders`);
      else toast.error("Lender search failed — try again");
      return acc;
    } finally { setFindingLenders(false); }
  }, [M, industry, runMarket]);

  const findMoreLenders = useCallback(async () => {
    setFindingLenders(true);
    try {
      const seen = new Set(lenders.map((l) => (l.name || "").toLowerCase().trim()));
      const acc = [];
      const batches = await Promise.all(LEND_MORE_SEGMENTS.map((seg) => lendBatch(M, industry, lenders, seg).catch(() => [])));
      batches.forEach((b) => { for (const l of b) { const k = (l.name || "").toLowerCase().trim(); if (k && !seen.has(k)) { seen.add(k); acc.push(l); } } });
      setLenders([...lenders, ...acc]);
      toast.success(`Added ${acc.length} more lenders (${lenders.length + acc.length} total)`);
    } finally { setFindingLenders(false); }
  }, [M, industry, lenders]);

  const runAll = useCallback(async () => {
    toast.info("Running Investors, Lenders & Market Comps in parallel…");
    await Promise.allSettled([runInvestors(), runLenders(), runMarket()]);
    toast.success("All AI passes complete");
  }, [runInvestors, runLenders, runMarket]);

  return (
    <DealContext.Provider
      value={{
        M, setM, setField, patch, dealId, setDealId, dealName, setDealName, scenario, setScenario, loadModel,
        scope, setScope, scopeOpts, V, scenFlash,
        active, setActive, navigate, asmpSection, setAsmpSection, saveState, syncNow, requestSave, startNewDeal, startRecap, platforms, refreshPlatforms,
        prospects, setProspects, prospectDrafts, setProspectDrafts, lenders, setLenders,
        industry, setIndustry,
        market, setMarket, marketLoading, marketStale, runMarket,
        findingInvestors, runInvestors, findMoreInvestors,
        findingLenders, runLenders, findMoreLenders,
        runAll,
      }}
    >
      {children}
    </DealContext.Provider>
  );
}

export function useDeal() {
  const ctx = useContext(DealContext);
  if (!ctx) throw new Error("useDeal must be used within DealProvider");
  return ctx;
}
